import { Link } from "react-router-dom";
import { PageShell } from "@/components/PageShell";
import { PRODUCTS } from "@/data/site";
import NAPKINS from "@/data/napkins.json";

export default function Products() {
  const napkinPreviews = NAPKINS.slice(0, 4);
  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-28 pb-8">
        <p className="overline">Catalog · 2026</p>
        <h1 className="mt-6 font-serif text-6xl md:text-8xl leading-[0.95] tracking-tighter">
          Our <span className="italic text-[#C27A62]">craft.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-[#5C665C] leading-relaxed">
          Four product lines, each engineered for a specific job — retail
          hand-off, foodservice, gifting and beverage. All compostable, all made
          from responsibly sourced pulp.
        </p>
      </section>

      {/* FEATURED SEASONAL COLLECTION */}
      <section
        data-testid="featured-napkins"
        className="max-w-7xl mx-auto px-6 lg:px-10 pt-4"
      >
        <Link
          to="/products/christmas-napkins"
          data-testid="featured-napkins-link"
          className="group grid md:grid-cols-12 gap-0 border border-[#D1C9B8] overflow-hidden bg-[#EBE5D9]"
        >
          <div className="md:col-span-7 relative">
            <div className="grid grid-cols-2 gap-0">
              {napkinPreviews.map((n) => (
                <div
                  key={n.code}
                  className="aspect-square bg-[#F4F1EB] border border-[#D1C9B8] overflow-hidden"
                >
                  <img
                    src={n.image}
                    alt={n.code}
                    loading="lazy"
                    className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5 p-8 md:p-12 flex flex-col justify-between">
            <div>
              <p className="overline text-[#C27A62]">New · Seasonal Collection</p>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-tight leading-[1]">
                Christmas<br />
                <span className="italic">napkin</span> line.
              </h2>
              <p className="mt-6 text-[#5C665C] leading-relaxed">
                {NAPKINS.length} festive designs across printed, gold-foil and shaped finishes — ready for hospitality and retail buyers.
              </p>
            </div>
            <span className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#1C2C22]">
              Explore the collection <span aria-hidden>→</span>
            </span>
          </div>
        </Link>
      </section>

      <section
        data-testid="products-list"
        className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 mt-24"
      >
        {PRODUCTS.map((p, i) => (
          <article
            key={p.slug}
            data-testid={`product-${p.slug}`}
            className={`grid md:grid-cols-12 gap-10 items-center py-16 md:py-24 border-t border-[#D1C9B8] ${
              i === PRODUCTS.length - 1 ? "border-b" : ""
            }`}
          >
            <div
              className={`md:col-span-6 zoom-wrap border border-[#D1C9B8] ${
                i % 2 === 1 ? "md:order-2" : ""
              } ${p.slug === "paper-plates" || p.slug === "paper-bags" || p.slug === "disposable-glass" ? "bg-[#EBE5D9]" : ""}`}
            >
              <img
                src={p.image}
                alt={p.name}
                loading="lazy"
                className={`w-full h-[380px] md:h-[520px] ${
                  p.slug === "paper-plates" || p.slug === "paper-bags" || p.slug === "disposable-glass"
                    ? "object-contain p-6 md:p-10"
                    : "object-cover"
                }`}
              />
            </div>
            <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1 md:pr-16" : "md:pl-16"}`}>
              <p className="overline">0{i + 1} · {p.tag}</p>
              <h2 className="font-serif text-5xl md:text-6xl tracking-tight mt-4">
                {p.name}
              </h2>
              <p className="mt-6 text-lg text-[#5C665C] leading-relaxed max-w-lg">
                {p.description}
              </p>
              <ul className="mt-8 grid grid-cols-1 gap-3 max-w-md">
                {p.highlights.map((h) => (
                  <li
                    key={h}
                    className="flex items-baseline gap-4 text-sm border-b border-[#D1C9B8] pb-3"
                  >
                    <span className="text-[#C27A62] font-serif text-xl leading-none">
                      ✦
                    </span>
                    <span className="text-[#1C2C22]">{h}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`product-${p.slug}-cta`}
                className="inline-flex items-center gap-3 mt-10 bg-[#1C2C22] text-[#F4F1EB] px-6 py-3 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#C27A62]"
              >
                Enquire about {p.name} →
              </Link>
              {p.slug === "paper-bags" && (
                <Link
                  to="/products/paper-bags-collection"
                  data-testid="paperbags-explore-collection"
                  className="inline-flex items-center gap-3 mt-4 md:mt-10 md:ml-4 text-xs uppercase tracking-[0.25em] border border-[#1C2C22] text-[#1C2C22] px-6 py-3 btn-lift hover:bg-[#1C2C22] hover:text-[#F4F1EB]"
                >
                  Explore the collection →
                </Link>
              )}
              {p.slug === "paper-plates" && (
                <Link
                  to="/products/paper-plates-collection"
                  data-testid="paperplates-explore-collection"
                  className="inline-flex items-center gap-3 mt-4 md:mt-10 md:ml-4 text-xs uppercase tracking-[0.25em] border border-[#1C2C22] text-[#1C2C22] px-6 py-3 btn-lift hover:bg-[#1C2C22] hover:text-[#F4F1EB]"
                >
                  Explore the collection →
                </Link>
              )}
              {p.slug === "gift-wrap-rolls" && (
                <Link
                  to="/products/gift-wrap-collection"
                  data-testid="giftwrap-explore-collection"
                  className="inline-flex items-center gap-3 mt-4 md:mt-10 md:ml-4 text-xs uppercase tracking-[0.25em] border border-[#1C2C22] text-[#1C2C22] px-6 py-3 btn-lift hover:bg-[#1C2C22] hover:text-[#F4F1EB]"
                >
                  Explore the collection →
                </Link>
              )}
              {p.slug === "disposable-glass" && (
                <Link
                  to="/products/disposable-glass-collection"
                  data-testid="disposableglass-explore-collection"
                  className="inline-flex items-center gap-3 mt-4 md:mt-10 md:ml-4 text-xs uppercase tracking-[0.25em] border border-[#1C2C22] text-[#1C2C22] px-6 py-3 btn-lift hover:bg-[#1C2C22] hover:text-[#F4F1EB]"
                >
                  Explore the collection →
                </Link>
              )}
            </div>
          </article>
        ))}
      </section>
    </PageShell>
  );
}
